import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { Spin, notification, Slider, Tooltip, Dropdown } from 'antd';
import {
  SoundOutlined,
  ClockCircleOutlined,
  CaretRightFilled,
  PauseOutlined,
  StarFilled,
  StarOutlined,
} from '@ant-design/icons';
import Icon from '@ant-design/icons';
import { ThemeModal, ThemeButton } from '../Styles/Theme';
import {
  CallInfoHeader,
  CallInfoItem,
  CallInfoLabel,
  CallInfoValue,
  EmptyTranscriptState,
  AudioPlayerWrapper,
  AudioPlayerMain,
  PlayButtonLarge,
  AudioProgressSection,
  AudioProgressBar,
  AudioTimeline,
  AudioControlsBar,
  SkipControls,
  SkipButton,
  SpeedSelector,
} from './CampaignCallsStyle';
import {
  getCallAudioApi,
  getCallAudioReset,
} from '../../store/actions/campaignAction';

// Custom SVG icons matching ElevenLabs style
const RotateCcwIcon = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </svg>
);

const RotateCwIcon = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12a9 9 0 1 1-9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
    <path d="M21 3v5h-5" />
  </svg>
);

// Helper to format duration
const formatDuration = (seconds) => {
  if (!seconds || seconds === 0 || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const AudioPlayer = (props) => {
  const {
    visible,
    onClose,
    campaignId,
    callId,
    callInfo,
    canDownload,
    onStarToggle,
    toggleCallStarState,
    getCallAudio,
    getCallAudioReset,
    getCallAudioState,
  } = props;

  const audioRef = useRef(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [downloading, setDownloading] = useState(false);

  // Fetch audio URL when modal opens
  useEffect(() => {
    if (visible && campaignId && callId) {
      getCallAudio(campaignId, callId);
    }
  }, [visible, campaignId, callId]);

  // Stop and reset audio when modal closes
  useEffect(() => {
    if (!visible) {
      // Stop audio playback
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.src = '';
      }
      // Reset all state
      setAudioUrl(null);
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setPlaybackRate(1);
      getCallAudioReset();
    }
  }, [visible, getCallAudioReset]);

  // Handle audio fetch response
  useEffect(() => {
    if (getCallAudioState.apiState === 'success' && getCallAudioState.data) {
      const audioData = getCallAudioState.data.data || getCallAudioState.data;

      if (audioData.status === 'ready' && audioData.url) {
        setAudioUrl(audioData.url);
      } else if (audioData.status === 'processing') {
        notification.info({
          message: 'Audio Processing',
          description: 'The audio is still being processed. Please try again later.',
        });
      } else if (audioData.status === 'failed') {
        notification.error({
          message: 'Audio Unavailable',
          description: audioData.error || 'Audio processing failed.',
        });
      }
      getCallAudioReset();
    } else if (getCallAudioState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: getCallAudioState.message || 'Failed to fetch audio',
      });
      getCallAudioReset();
    }
  }, [getCallAudioState, getCallAudioReset]);

  // Audio event handlers
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  };

  const handlePlayPause = () => {
    if (!audioRef.current || !audioUrl) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (value) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value;
      setCurrentTime(value);
    }
  };

  const handleSkipBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10);
    }
  };

  const handleSkipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 10);
    }
  };

  const handlePlaybackRateChange = (rate) => {
    setPlaybackRate(rate);
    if (audioRef.current) {
      audioRef.current.playbackRate = rate;
    }
  };

  const handleDownload = async () => {
    if (!audioUrl) {
      notification.warning({ message: 'No audio available to download' });
      return;
    }

    setDownloading(true);
    try {
      const response = await fetch(audioUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audio_${callInfo?.participantId || callId}_${callId}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      notification.success({ message: 'Audio downloaded successfully' });
    } catch (error) {
      console.error('Download error:', error);
      notification.error({ message: 'Failed to download audio' });
    } finally {
      setDownloading(false);
    }
  };

  const isLoading = getCallAudioState.apiState === 'loading';

  const playbackRates = [0.75, 1, 1.25, 1.5, 2];

  const downloadMenuItems = [
    {
      key: 'mp3',
      label: 'Download as MP3',
      onClick: () => handleDownload(),
    },
  ];

  return (
    <ThemeModal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <SoundOutlined />
          <span>Call Audio</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={600}
      className="audio-player-modal"
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            {onStarToggle && (
              <ThemeButton
                className="noBackground"
                icon={callInfo?.isStarred ? <StarFilled style={{ color: '#FAAD14' }} /> : <StarOutlined />}
                onClick={() => {
                  const isToggling = toggleCallStarState?.apiState === 'loading' && toggleCallStarState?.callId === callId;
                  if (!isToggling) onStarToggle();
                }}
                loading={toggleCallStarState?.apiState === 'loading' && toggleCallStarState?.callId === callId}
              >
                {callInfo?.isStarred ? 'Remove Star' : 'Mark as Starred'}
              </ThemeButton>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {canDownload && audioUrl && (
              <Dropdown
                menu={{ items: downloadMenuItems }}
                placement="topRight"
                trigger={['click']}
              >
                <ThemeButton loading={downloading}>
                  Download
                </ThemeButton>
              </Dropdown>
            )}
            <ThemeButton onClick={onClose}>
              Close
            </ThemeButton>
          </div>
        </div>
      }
    >
      <Spin spinning={isLoading} tip="Loading audio...">
        {/* Call Info Header */}
        <CallInfoHeader>
          <CallInfoItem>
            <CallInfoLabel>Participant ID</CallInfoLabel>
            <CallInfoValue>{callInfo?.participantId || '-'}</CallInfoValue>
          </CallInfoItem>
          <CallInfoItem>
            <CallInfoLabel>Call Date</CallInfoLabel>
            <CallInfoValue>{callInfo?.callDate || '-'}</CallInfoValue>
          </CallInfoItem>
          <CallInfoItem>
            <CallInfoLabel>Duration</CallInfoLabel>
            <CallInfoValue>
              <ClockCircleOutlined style={{ marginRight: '4px' }} />
              {formatDuration(callInfo?.duration)}
            </CallInfoValue>
          </CallInfoItem>
        </CallInfoHeader>

        {/* Audio Player */}
        {audioUrl ? (
          <AudioPlayerWrapper>
            <audio
              ref={audioRef}
              src={audioUrl}
              onLoadedMetadata={handleLoadedMetadata}
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleEnded}
            />

            {/* Main Player Area */}
            <AudioPlayerMain>
              <PlayButtonLarge onClick={handlePlayPause}>
                {isPlaying ? <PauseOutlined /> : <CaretRightFilled />}
              </PlayButtonLarge>

              <AudioProgressSection>
                <AudioProgressBar>
                  <Slider
                    min={0}
                    max={duration || 100}
                    value={currentTime}
                    onChange={handleSeek}
                    tooltip={{ formatter: (val) => formatDuration(val) }}
                  />
                </AudioProgressBar>
                <AudioTimeline>
                  <span>{formatDuration(currentTime)}</span>
                  <span>{formatDuration(duration)}</span>
                </AudioTimeline>
              </AudioProgressSection>
            </AudioPlayerMain>

            {/* Controls Bar */}
            <AudioControlsBar>
              <SkipControls>
                <Tooltip title="Rewind 10 seconds">
                  <SkipButton onClick={handleSkipBackward}>
                    <Icon component={RotateCcwIcon} />
                  </SkipButton>
                </Tooltip>
                <Tooltip title="Forward 10 seconds">
                  <SkipButton onClick={handleSkipForward}>
                    <Icon component={RotateCwIcon} />
                  </SkipButton>
                </Tooltip>
              </SkipControls>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', fontWeight: 500, color: '#83899F' }}>Speed</span>
                <SpeedSelector>
                  {playbackRates.map((rate) => (
                    <button
                      key={rate}
                      onClick={() => handlePlaybackRateChange(rate)}
                      className={playbackRate === rate ? 'active' : ''}
                    >
                      {rate}x
                    </button>
                  ))}
                </SpeedSelector>
              </div>
            </AudioControlsBar>
          </AudioPlayerWrapper>
        ) : (
          <EmptyTranscriptState>
            <SoundOutlined />
            <h4>No Audio Available</h4>
            <p>
              {isLoading
                ? 'Loading audio...'
                : 'The audio for this call is not yet available or failed to load.'}
            </p>
          </EmptyTranscriptState>
        )}
      </Spin>
    </ThemeModal>
  );
};

const mapStateToProps = (state) => ({
  getCallAudioState: state.campaign.getCallAudioState,
});

const mapDispatchToProps = (dispatch) => ({
  getCallAudio: (campaignId, callId) =>
    dispatch(getCallAudioApi(campaignId, callId)),
  getCallAudioReset: () => dispatch(getCallAudioReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AudioPlayer);
